/** @jsxImportSource @emotion/react */
import React from 'react';
import { Button, Descriptions, Empty, Space, Tag, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const styles = {
  zeroMarginTag: css({ margin: 0 }),
  itemContainer: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }),
};

interface TaxonomyAttributesListProps {
  attributes: any[];
  loading?: boolean;
  onEdit: (item: any) => void;
}

export const TaxonomyAttributesList: React.FC<TaxonomyAttributesListProps> = ({
  attributes,
  loading,
  onEdit,
}) => {
  const { t } = useTranslation('crops');
  if (loading) {
    return null;
  }

  if (attributes.length === 0) {
    return <Empty description={t('attributes.emptyLevel')} />;
  }

  return (
    <Descriptions bordered size="small" column={1}>
      {attributes.map((item) => {
        const binding = item.binding;
        const currentValue = binding?.choiceTitle ?? binding?.choiceValue ?? binding?.customValue ?? binding?.value ?? '—';
        const currentCode = binding?.choiceCode;
        const displayValue = currentCode && currentCode !== currentValue ? `${currentValue} (${currentCode})` : currentValue;
        
        return (
          <Descriptions.Item
            key={String(item.attribute.id)}
            label={
              <Space size={4}>
                <span>{item.attribute.name}</span>
                {item.attribute.scopeType === 'farm' && (
                  <Tag color="green" css={styles.zeroMarginTag}>{t('nav.farm')}</Tag>
                )}
              </Space>
            }
          >
            <div css={styles.itemContainer}>
              <Text>{displayValue}</Text>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEdit(item)}
              >
                {t('common:actions.edit')}
              </Button>
            </div>
          </Descriptions.Item>
        );
      })}
    </Descriptions>
  );
};
